/* eslint-disable max-lines */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import { CommentableType, LocalFile, ObservationStatus, ProjectFilePosition, RemoteFile } from '@types'

import { Modal } from '@components/Modal'
import { SendProps, WriteComment } from '@components/WriteComment'

import { TradeTag } from '@modules/_shared/components/TradeTag'
import { LocalizationEdit } from '@modules/_shared/components/LocalizationEdit'

import { useIOSPreventAutoZooming } from '@utils/hooks/useIOSPreventZooming'

import { useModal } from '@hooks/app/modal.hook'
import { useCreateComment } from '@hooks/comment/create-comment.hook'
import { useFilePreviews } from '@hooks/file/file-preview.hook'
import { useObservationWithComments } from '@hooks/observation/observation-with-comments.hook'
import { useUpdateObservation } from '@hooks/observation/update-observation.hook'
import { useUpdateStatusObservation } from '@hooks/observation/update-status-observation.hook'
import { useProjectGuards } from '@hooks/project/project-guards.hook'

import { ObservationDetails } from '../../components/ObservationDetails'
import { FormData, ObservationEdit } from '../../components/ObservationEdit'

import { AddCommentContainer, Container, LabelCompany, SubTitleContainer } from './mobile.edit-observation.styled'

type Mode = 'details' | 'edit' | 'localization'

interface FormTitles {
  details: string
  edit: string
  localization: string
}

interface FormActions {
  details?: string
  edit: string
  localization?: string
}

export const MobileEditObservationPage = (): JSX.Element => {
  const { hideModal } = useModal()
  const { t } = useTranslation()
  const { projectId } = useParams()
  const [searchParams] = useSearchParams()
  const scrollViewRef = useRef<HTMLDivElement | null>()

  const observationId = searchParams.get('observationId')

  const [mode, setMode] = useState<Mode>('details')
  const [formMode, setFormMode] = useState<'form' | 'localization'>('form')
  const [files, setFiles] = useState<(LocalFile | RemoteFile)[]>([])
  const [planFile, setPlanFile] = useState<RemoteFile>()

  const [formData, setFormData] = useState<FormData>({
    name: '',
    trade: undefined,
    company: undefined,
    phase: undefined,
    limitDate: undefined,
    projectFile: undefined,
    projectFilePosition: undefined,
  })

  useIOSPreventAutoZooming()

  const { data, loading, error, refetch } = useObservationWithComments(observationId || '')
  const { createComment } = useCreateComment()
  const { updateObservation } = useUpdateObservation()
  const { updateStatusObservation } = useUpdateStatusObservation({
    onCompleted: () => refetch(observationId || ''),
  })

  const { getProjectGuards } = useProjectGuards({ projectId })
  const { canCommentObservation, canEditObservationStatus, canEditObservation } = getProjectGuards()

  const isLoading = loading ? 1 : error ? -1 : 0
  const observation = data?.observation

  const { loadFilePreviews: loadPlanPreview } = useFilePreviews({
    files: formData?.projectFile?.file ? ([formData?.projectFile?.file] as RemoteFile[]) : undefined,
    onFilesLoaded: ([file]) => setPlanFile(file as RemoteFile),
  })

  const { loadFilePreviews: loadPicturesPreviews } = useFilePreviews({
    files: observation?.files as RemoteFile[],
    onFilesLoaded: setFiles,
  })

  const titles: FormTitles = {
    details: observation?.idNumber ? `Obs #${observation?.idNumber} - ${observation?.name}` : '',
    edit: t('modules_project-observations_pages_edit-observation_title'),
    localization: t('modules_project-observations_pages_edit_observation_localization_title'),
  }

  const submitLabels: FormActions = {
    details: undefined,
    edit: t('modules_project-observations_pages_edit-observation_submit_title'),
    localization: undefined,
  }

  useEffect(() => {
    observationId && refetch(observationId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [observationId])

  useEffect(() => {
    if (formData.projectFile) {
      loadPlanPreview()
    }
    if (observation?.files) {
      loadPicturesPreviews()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.projectFile, observation?.files])

  useEffect(() => {
    if (observation) {
      setFormData({
        name: observation.name,
        trade: observation.trade,
        company: observation.company,
        phase: observation.phase,
        limitDate: observation.limitDate,
        projectFile: observation.projectFile,
        projectFilePosition: observation.projectFilePositions ? observation.projectFilePositions[0] : undefined,
      })
    }
  }, [observation])

  const handleSubmitEdit = () => {
    if (!projectId || !observationId) return

    if (formMode === 'localization') {
      setFormMode('form')
      return
    }

    updateObservation(
      {
        observationId,
        observation: {
          name: formData.name,
          tradeKey: formData.trade!.key,
          companyId: formData.company?.id,
          phaseKey: formData.phase?.key,
          limitDate: formData.limitDate,
          projectFileId: formData.projectFilePosition ? formData.projectFile?.id : undefined,
          projectFilePositions: formData.projectFilePosition
            ? [
                {
                  x: formData.projectFilePosition.x,
                  y: formData.projectFilePosition.y,
                },
              ]
            : undefined,
        },
      },
      files,
    )

    if (mode === 'edit' || mode === 'localization') {
      setMode('details')
    }
  }

  const handleStatusChange = (newStatus?: ObservationStatus): void => {
    if (newStatus && observationId) {
      updateStatusObservation({ observationId, status: newStatus })
    }
  }

  const handleCommentSubmit = async ({ value, files, reset, setLoading }: SendProps) => {
    if (!observationId) return

    setLoading(true)
    try {
      await createComment(
        { commentableId: observationId, commentableType: CommentableType.Observation, value },
        files[0],
      )
      reset()
    } catch (error) {
      console.error('Error creating comment:', error)
    }
    setLoading(false)
  }

  const handleOnPrevious = () => {
    if (formMode === 'localization') {
      setFormMode('form')
      return
    }

    if (mode === 'edit') {
      resetFormData()
      setMode('details')
    }

    if (mode === 'localization') {
      handleSubmitEdit()
    }
  }

  const resetFormData = () => {
    setFormData({
      name: observation?.name || '',
      trade: observation?.trade,
      company: observation?.company,
      phase: observation?.phase,
      limitDate: observation?.limitDate,
      projectFile: observation?.projectFile,
      projectFilePosition: observation?.projectFilePositions ? observation.projectFilePositions[0] : undefined,
    })
  }

  const renderSubtitle = () => {
    if (!observation || mode !== 'details') return undefined

    return (
      <SubTitleContainer>
        <TradeTag trade={observation.trade} />
        <LabelCompany>{observation.company?.name}</LabelCompany>
      </SubTitleContainer>
    )
  }

  const renderFooter = () => {
    if (mode !== 'details') return undefined

    return (
      <AddCommentContainer>
        <WriteComment onClickSend={handleCommentSubmit} />
      </AddCommentContainer>
    )
  }

  const showFooter = () => {
    if (formMode === 'localization') return false

    const footerRules: {
      [key: string]: () => boolean
    } = {
      details: () => canCommentObservation,
      edit: () => canEditObservation,
    }

    const rule = footerRules[mode]
    return rule ? rule() : false
  }

  return (
    <Modal
      refScrollContainer={(ref) => (scrollViewRef.current = ref)}
      title={titles[mode]}
      onSubmitLabel={submitLabels[mode]}
      showPrevious={mode !== 'details'}
      onSubmit={handleSubmitEdit}
      onPrevious={handleOnPrevious}
      onClose={() => hideModal('update_observation')}
      showClose={formMode !== 'localization' && mode !== 'localization'}
      SubTitleComponent={renderSubtitle()}
      FooterComponent={renderFooter()}
      showFooter={showFooter()}
      autoHideOnSubmit={false}
    >
      <Container>
        {!observation ? null : (
          <>
            {mode === 'details' && (
              <ObservationDetails
                observation={observation}
                loading={isLoading}
                canEditObservation={canEditObservation}
                canEditObservationStatus={canEditObservationStatus}
                onStatusChange={handleStatusChange}
                onRefresh={() => refetch(observationId || '')}
                onEditClick={() => setMode('edit')}
                onLocalizationClick={() => setMode('localization')}
              />
            )}

            {mode === 'edit' && (
              <ObservationEdit
                disableSearches
                disabledAddCompany
                planFile={planFile}
                projectId={projectId || ''}
                observation={observation}
                formData={formData}
                mode={formMode}
                files={files}
                onFormChange={setFormData}
                onFilesChange={setFiles}
                onAddLocalization={() => setFormMode('localization')}
              />
            )}

            {mode === 'localization' && (
              <LocalizationEdit
                markerType="observation"
                file={planFile}
                projectFile={formData.projectFile}
                projectId={projectId || ''}
                data={observation}
                onAdd={(projectFilePosition: ProjectFilePosition) => setFormData({ ...formData, projectFilePosition })}
                onRemove={() => setFormData({ ...formData, projectFilePosition: undefined })}
                onChange={(projectFilePosition: ProjectFilePosition) =>
                  setFormData({ ...formData, projectFilePosition })
                }
                onChangeProjectFile={(_, projectFile) => {
                  setFormData((prev) => ({
                    ...prev,
                    projectFile,
                    projectFilePosition: projectFile ? prev.projectFilePosition : undefined,
                  }))
                }}
              />
            )}
          </>
        )}
      </Container>
    </Modal>
  )
}
